import { useContext, useEffect, useState, useMemo, useCallback } from "react";
import { useNavigate, useParams, useSearchParams, Link, useBlocker } from "react-router";

import { Button } from "primereact/button";
import { ConfirmDialog, confirmDialog } from "primereact/confirmdialog";
import { Dialog } from "primereact/dialog";
import { FormStepper, ValidationMessage } from "sj-ui-toolkit/src/components";
import { useBreadcrumb } from "sj-ui-toolkit/src/atlas/hooks/useBreadcrumbs";
import { themeClasses } from "sj-ui-toolkit/src/atlas/utils";
import { useToast } from "sj-ui-toolkit/src/hooks";
import { AdminAppRoutes } from "sj-ui-toolkit/src/constants/routes";
import C from "classnames";

import { PathsContext } from "@/context/PathsContext";
import {
  loadPathByIdAsync,
  createPathAsync,
  updatePathAsync,
  formatPathDataForAPI,
  validatePathName,
  validatePathDescription,
} from "@/api/paths";
import { loadLevelsAsync, isActivityLevel, levelsWithoutSkipped, type Level } from "@/api/levels";

import PathDetailsStep, { type DetailsValues } from "@/components/paths/PathDetailsStep";
import PathContentStep, {
  type ContentValues,
  type SelectedLesson,
} from "@/components/paths/PathContentStep";
import ActivityPathContentStep, {
  type ActivityContentValues,
} from "@/components/paths/ActivityPathContentStep";
import PathSettingsStep, { type SettingsValues } from "@/components/paths/PathSettingsStep";
import ReviewStep from "@/components/paths/ReviewStep";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface FormValues {
  details: DetailsValues;
  content: ContentValues & ActivityContentValues;
  settings: SettingsValues & { pathArchived?: boolean; pathRestricted?: boolean };
}

interface ValidationErrors {
  details: {
    name: { hasError: boolean; message: string };
    description: { hasError: boolean; message: string };
    level: { hasError: boolean; message: string };
  };
  content: { hasError: boolean; message: string };
}

const INITIAL_FORM_VALUES: FormValues = {
  details: { name: "", description: "", levelId: "", levelName: "", rankName: "" },
  content: {
    selectedLessons: [],
    orderedLessons: [],
    lessonGroupIds: [],
    requiredLessonIds: [],
    totalPoints: 0,
  },
  settings: {
    requireRetake: false,
    includeDescriptionOnCertificate: false,
    activatePath: false,
    restrictPath: false,
  },
};

const INITIAL_VALIDATION_ERRORS: ValidationErrors = {
  details: {
    name: { hasError: true, message: "Path name is required" },
    description: { hasError: true, message: "Path description is required" },
    level: { hasError: true, message: "Level is required" },
  },
  content: { hasError: true, message: "Please select at least one lesson" },
};

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

const CUSTOM_PATH_DRAFT_PREFIX = "sj_ai_custom_path_draft_";

const PathsNew = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const { showToast } = useToast();

  const isNew = !id;

  const { allPathNames, allPaths, loadPathsAsync } = useContext(PathsContext);

  // Read lesson keys from sessionStorage if navigated here from AI Chat
  const preselectedLessonKeys = useMemo(() => {
    const uuid = searchParams.get("customPathDraft");
    if (!uuid) return [];
    const storageKey = `${CUSTOM_PATH_DRAFT_PREFIX}${uuid}`;
    const stored = sessionStorage.getItem(storageKey);
    if (!stored) return [];
    sessionStorage.removeItem(storageKey);
    try {
      return JSON.parse(stored) as string[];
    } catch {
      return [];
    }
  }, []);

  const [levels, setLevels] = useState<Level[]>([]);
  const [levelsLoading, setLevelsLoading] = useState(false);
  const [isQuest, setIsQuest] = useState(false);
  const contentTypeLabel = isQuest ? "Quest" : "Path";

  const [currentStep, setCurrentStep] = useState(0);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showValidationMessages, setShowValidationMessages] = useState(false);
  const [originalPathName, setOriginalPathName] = useState<string | null>(null);

  const [validationErrors, setValidationErrors] =
    useState<ValidationErrors>(INITIAL_VALIDATION_ERRORS);

  const [selectedLessonsToAdd, setSelectedLessonsToAdd] = useState<SelectedLesson[]>([]);
  const [selectedLessonsToRemove, setSelectedLessonsToRemove] = useState<SelectedLesson[]>([]);

  const [formValues, setFormValues] = useState<FormValues>(INITIAL_FORM_VALUES);
  const [isDirty, setIsDirty] = useState(false);

  // Derived: true when level is activity (brown/black) and not a quest
  const isActivityPath = useMemo(
    () => !isQuest && isActivityLevel({ rankName: formValues.details.rankName }),
    [isQuest, formValues.details.rankName]
  );

  const minPoints = useMemo(() => {
    if (!isActivityPath || formValues.details.rankName !== "black") return 0;
    const brownPath = allPaths.find(p => p.rankName === "brown" && p.activated);
    return brownPath?.totalPoints ?? 0;
  }, [isActivityPath, formValues.details.rankName, allPaths]);

  const conflictingActivePath = useMemo(() => {
    if (!isActivityPath || !formValues.settings.activatePath) return null;
    return (
      allPaths.find(p => {
        const isSameRank = p.rankName === formValues.details.rankName;
        const isActive = p.activated;
        const isCurrentPath = !isNew && id != null && String(p.id) === String(id);
        return isSameRank && isActive && !isCurrentPath;
      }) ?? null
    );
  }, [
    isActivityPath,
    formValues.settings.activatePath,
    formValues.details.rankName,
    allPaths,
    isNew,
    id,
  ]);

  // Navigation blocker (required by migration skill for FormStepper pages)
  const blocker = useBlocker(isDirty && !isSubmitting);

  useBreadcrumb(
    isNew
      ? {
          label: `New ${contentTypeLabel}`,
          route: AdminAppRoutes.routes.paths.routes.new.route,
          display: "page",
        }
      : {
          label: "Edit",
          route: AdminAppRoutes.routes.paths.routes.edit.route.replace(":id", id ?? ""),
          display: "page",
        }
  );

  // Show validation messages when reaching review step
  useEffect(() => {
    if (currentStep === 3) setShowValidationMessages(true);
  }, [currentStep]);

  // Load levels once on mount
  useEffect(() => {
    setLevelsLoading(true);
    loadLevelsAsync()
      .then(data => setLevels(levelsWithoutSkipped(data.courses ?? [])))
      .catch(err => console.error("Failed to load levels:", err))
      .finally(() => setLevelsLoading(false));
  }, []);

  useEffect(() => {
    void loadPathsAsync(true);
    if (isNew) setIsQuest(searchParams.get("type") === "quest");
  }, []);

  useEffect(() => {
    if (!isNew && id) void loadPathData(id);
  }, [id, isNew]);

  useEffect(() => {
    if (isActivityPath && minPoints > 0) {
      setFormValues(prev => ({
        ...prev,
        content: {
          ...prev.content,
          totalPoints: Math.max(prev.content.totalPoints ?? 0, minPoints),
        },
      }));
    }
  }, [minPoints, isActivityPath]);

  // Re-validate when isQuest changes (label changes)
  useEffect(() => {
    validateSection("details", formValues.details);
  }, [isQuest]);

  // Re-validate content when activity path status changes
  useEffect(() => {
    validateSection("content", formValues.content);
  }, [isActivityPath]);

  // Re-validate content when totalPoints changes (for activity paths)
  useEffect(() => {
    if (isActivityPath) {
      validateSection("content", { ...formValues.content, isActivity: true });
    }
  }, [formValues.content.totalPoints]);

  const loadPathData = async (pathId: string) => {
    try {
      setIsLoading(true);
      const response = await loadPathByIdAsync(pathId);
      const pathData = response.role;

      const lessons = pathData.versions?.[0]?.lessons ?? pathData.lessons ?? [];
      const selectedLessons: SelectedLesson[] = lessons.map(l => ({
        id: l.id,
        name: l.name,
        description: l.description,
        duration: l.durationNumber,
        required: l.required,
        status: l.status,
        lessonKey: l.lessonKey,
      }));
      const lessonGroupIds = lessons.map(l => l.id);
      const requiredLessonIds = lessons.filter(l => l.required).map(l => l.id);

      const newValues: FormValues = {
        details: {
          name: pathData.name ?? "",
          description: pathData.description ?? "",
          levelId: pathData.levelId ?? "",
          levelName: pathData.levelName ?? "",
          rankName: pathData.rankName ?? "",
        },
        content: {
          selectedLessons,
          orderedLessons: [...selectedLessons],
          lessonGroupIds,
          requiredLessonIds,
          totalPoints: pathData.totalPoints ?? 0,
        },
        settings: {
          requireRetake: pathData.reassign ?? false,
          includeDescriptionOnCertificate: pathData.includeCustomCertDescription ?? false,
          activatePath: pathData.activated ?? false,
          restrictPath: pathData.restricted ?? false,
          pathArchived: pathData.archived ?? false,
          pathRestricted: pathData.restricted ?? false,
        },
      };

      setFormValues(newValues);
      setIsDirty(false);
      setOriginalPathName(pathData.name ?? null);
      setIsQuest(!pathData.type);

      const isActivity = isActivityLevel({ rankName: pathData.rankName });
      validateSection("details", newValues.details, { originalPathName: pathData.name ?? null });
      if (isActivity) {
        validateSection("content", { ...newValues.content, isActivity: true });
      } else if (selectedLessons.length > 0) {
        validateSection("content", newValues.content);
      }
    } catch (error) {
      console.error("Failed to load path:", error);
      showToast({
        severity: "error",
        summary: "Error",
        detail: "Failed to load path data",
        life: 3000,
      });
      navigate(AdminAppRoutes.routes.paths.routes.detail.route.replace(":id", pathId));
    } finally {
      setIsLoading(false);
    }
  };

  const validateSection = useCallback(
    (
      section: "details" | "content",
      vals: DetailsValues | (ContentValues & ActivityContentValues & { isActivity?: boolean }),
      overrides: { originalPathName?: string | null } = {}
    ) => {
      const effectiveOriginalName = overrides.originalPathName ?? originalPathName;
      switch (section) {
        case "details": {
          const detailVals = vals as DetailsValues & { isActivity?: boolean };
          const nameValidation = validatePathName(
            detailVals.name,
            allPathNames,
            effectiveOriginalName,
            contentTypeLabel
          );
          const descValidation = validatePathDescription(
            detailVals.description as string,
            contentTypeLabel
          );
          const hasLevelError = !isQuest && !detailVals.levelId;
          setValidationErrors(prev => ({
            ...prev,
            details: {
              name: { hasError: !nameValidation.isValid, message: nameValidation.message },
              description: { hasError: !descValidation.isValid, message: descValidation.message },
              level: { hasError: hasLevelError, message: "Level is required" },
            },
          }));
          break;
        }
        case "content": {
          const contentVals = vals as ContentValues & { isActivity?: boolean };
          const treatAsActivity =
            contentVals.isActivity ||
            (!isQuest && isActivityLevel({ rankName: formValues.details.rankName }));

          if (treatAsActivity) {
            const hasPoints = (contentVals.totalPoints ?? 0) > 0;
            setValidationErrors(prev => ({
              ...prev,
              content: { hasError: !hasPoints, message: "Please set required points to continue" },
            }));
          } else {
            const hasContent = (contentVals.selectedLessons?.length ?? 0) > 0;
            setValidationErrors(prev => ({
              ...prev,
              content: { hasError: !hasContent, message: "Please select at least one lesson" },
            }));
          }
          break;
        }
      }
    },
    [allPathNames, originalPathName, isQuest, formValues.details.rankName, contentTypeLabel]
  );

  const handleFormChange = (section: keyof FormValues, newValues: FormValues[keyof FormValues]) => {
    setFormValues(prev => ({ ...prev, [section]: newValues }));
    setIsDirty(true);
    if (section === "details" || section === "content") {
      validateSection(
        section,
        newValues as unknown as
          | DetailsValues
          | (ContentValues & ActivityContentValues & { isActivity?: boolean })
      );
    }
  };

  const isFormValid = useMemo(() => {
    const levelValid = isQuest || !validationErrors.details.level.hasError;
    return (
      !validationErrors.details.name.hasError &&
      !validationErrors.details.description.hasError &&
      levelValid &&
      !validationErrors.content.hasError
    );
  }, [validationErrors, isQuest]);

  const handleSubmit = async () => {
    if (!isFormValid) {
      showToast({
        severity: "error",
        summary: "Validation error",
        detail: `Please fill in all required fields before creating the ${contentTypeLabel.toLowerCase()}`,
      });
      return;
    }

    try {
      setIsSubmitting(true);

      const pathData: Record<string, unknown> = {
        name: formValues.details.name,
        description: formValues.details.description,
        levelId: isQuest ? null : formValues.details.levelId,
        levelType: isQuest ? "quest" : "course",
        totalPoints: isActivityPath ? (formValues.content.totalPoints ?? 0) : 0,
        reassign: formValues.settings.requireRetake,
        includeCustomCertDescription: formValues.settings.includeDescriptionOnCertificate,
        lessonGroupIds: isActivityPath ? [] : (formValues.content.lessonGroupIds ?? []),
        requiredLessonIds: isActivityPath ? [] : (formValues.content.requiredLessonIds ?? []),
        activated: formValues.settings.activatePath,
        archived: false,
        ...(isQuest && isNew && { type: null }),
      };

      const jsonData = formatPathDataForAPI(pathData as Parameters<typeof formatPathDataForAPI>[0]);
      let newPathId: string | number | undefined;

      if (isNew) {
        const data = await createPathAsync(jsonData);
        newPathId = data.role.id;
      } else {
        const data = await updatePathAsync(id!, jsonData);
        newPathId = data.role.id;
      }

      showToast({
        severity: "success",
        summary: isNew ? `${contentTypeLabel} Created!` : `${contentTypeLabel} Updated!`,
        detail: `Your learning ${contentTypeLabel.toLowerCase()} has been successfully ${isNew ? "created" : "updated"}.`,
      });

      navigate(
        AdminAppRoutes.routes.paths.routes.detail.route.replace(":id", String(newPathId ?? id))
      );
    } catch (error) {
      const activatedErrors = (
        error as { response?: { data?: { errors?: { activated?: string[] } } } }
      )?.response?.data?.errors?.activated;
      if (activatedErrors?.length) {
        const levelName = formValues.details.levelName || formValues.details.rankName;
        showToast({
          severity: "error",
          summary: "Active path conflict",
          detail: `There is already an active ${levelName} path. Please deactivate it before creating a new one.`,
          life: 8000,
        });
        return;
      }

      const errorMessage =
        (error as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        (error as Error).message;
      console.error("Error:", errorMessage);
      showToast({
        severity: "error",
        summary: "Error",
        detail: `Failed to ${isNew ? "create" : "update"} ${contentTypeLabel.toLowerCase()}: ${errorMessage}`,
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    confirmDialog({
      message: "Changes you made will not be saved.",
      header: "Are you sure?",
      defaultFocus: "reject",
      acceptClassName: "p-button-danger",
      icon: "pi pi-exclamation-circle",
      acceptLabel: "Confirm",
      rejectLabel: "Cancel",
      headerStyle: { border: "none" },
      accept: () => {
        if (!isNew && id) {
          navigate(AdminAppRoutes.routes.paths.routes.detail.route.replace(":id", id));
        } else {
          navigate(
            isQuest
              ? AdminAppRoutes.routes.paths.routes.quests.route
              : AdminAppRoutes.routes.paths.routes.custom.route
          );
        }
      },
    });
  };

  const detailsHeader = showValidationMessages
    ? `${contentTypeLabel} Details${
        !validationErrors.details.name.hasError &&
        !validationErrors.details.description.hasError &&
        (isQuest || !validationErrors.details.level.hasError)
          ? " ✓"
          : " ✗"
      }`
    : `${contentTypeLabel} Details`;

  const contentHeader = showValidationMessages
    ? `${contentTypeLabel} ${isActivityPath ? "Activity Points" : "Content"}${!validationErrors.content.hasError ? " ✓" : " ✗"}`
    : `${contentTypeLabel} ${isActivityPath ? "Activity Points" : "Content"}`;

  const steps = [
    {
      header: detailsHeader,
      title: `${contentTypeLabel} Details`,
      content: (
        <PathDetailsStep
          key="path-details"
          values={formValues.details}
          handleChange={newValues => handleFormChange("details", newValues)}
          showValidationMessages={showValidationMessages}
          validationErrors={validationErrors.details}
          isLoading={isLoading}
          contentTypeLabel={contentTypeLabel}
          existingPathNames={allPathNames}
          currentPathName={originalPathName}
          isNew={isNew}
          levels={levels}
          levelsLoading={levelsLoading}
        />
      ),
    },
    {
      header: contentHeader,
      title: isActivityPath
        ? `${contentTypeLabel} Activity Points`
        : `${contentTypeLabel} Content & Order`,
      content: isActivityPath ? (
        <ActivityPathContentStep
          values={formValues.content}
          handleChange={newValues => handleFormChange("content", newValues)}
          showValidationMessages={showValidationMessages}
          validationErrors={validationErrors.content}
          minPoints={minPoints}
        />
      ) : (
        <PathContentStep
          values={formValues.content}
          handleChange={newValues => handleFormChange("content", newValues)}
          showValidationMessages={showValidationMessages}
          validationErrors={validationErrors.content}
          selectedLessonsToAdd={selectedLessonsToAdd}
          setSelectedLessonsToAdd={setSelectedLessonsToAdd}
          selectedLessonsToRemove={selectedLessonsToRemove}
          setSelectedLessonsToRemove={setSelectedLessonsToRemove}
          preselectedLessonKeys={preselectedLessonKeys}
        />
      ),
    },
    {
      header: showValidationMessages
        ? `${contentTypeLabel} Settings ✓`
        : `${contentTypeLabel} Settings`,
      title: `${contentTypeLabel} Settings`,
      content: (
        <PathSettingsStep
          values={formValues.settings}
          handleChange={newValues => handleFormChange("settings", newValues)}
          isNew={isNew}
          pathArchived={formValues.settings.pathArchived}
          contentTypeLabel={contentTypeLabel}
        />
      ),
    },
    {
      header: "Review",
      title: `Review ${contentTypeLabel}`,
      content: (
        <>
          <ReviewStep
            values={formValues}
            onStepChange={setCurrentStep}
            validationErrors={validationErrors}
            contentTypeLabel={contentTypeLabel}
            isActivityPath={isActivityPath}
          />
          <div className="absolute bottom-8 right-0 flex flex-col gap-2">
            {conflictingActivePath && (
              <ValidationMessage
                severity="warn"
                text={
                  <span>
                    <span className="font-bold">&ldquo;{conflictingActivePath.name}&rdquo;</span> is
                    the currently active{" "}
                    <span className="font-bold">{formValues.details.levelName}</span> path. You need
                    to{" "}
                    <Link
                      to={AdminAppRoutes.routes.paths.routes.detail.route.replace(
                        ":id",
                        String(conflictingActivePath.id)
                      )}
                      className="!underline font-bold hover:opacity-80"
                      rel="noopener noreferrer"
                    >
                      deactivate it
                    </Link>{" "}
                    before you can activate{" "}
                    <span className="font-bold">
                      &ldquo;{formValues.details.name || `this ${contentTypeLabel.toLowerCase()}`}
                      &rdquo;
                    </span>
                    .
                  </span>
                }
              />
            )}
            {!isFormValid && (
              <ValidationMessage
                severity="warn"
                text={`Please fill in all required fields before creating the ${contentTypeLabel.toLowerCase()}`}
              />
            )}
          </div>
        </>
      ),
    },
  ];

  return (
    <>
      <div className="mb-4 flex items-center justify-between">
        <h1>{isNew ? `Create New ${contentTypeLabel}` : `Edit ${contentTypeLabel}`}</h1>
        <Button
          label=""
          icon="pi pi-times"
          iconPos="right"
          onClick={handleCancel}
          severity="contrast"
          aria-label="Cancel"
          data-testid="path-form-close-button"
        />
      </div>

      <div
        data-testid="path-form-stepper"
        className={C("p-4 min-h-[60vh] flex flex-col", themeClasses.bgElevation1)}
      >
        <div className="flex-1 flex flex-col">
          <FormStepper
            steps={steps}
            currentStep={currentStep}
            setCurrentStep={setCurrentStep}
            handleSubmit={() => void handleSubmit()}
            isNew={isNew}
            isSubmitting={isSubmitting}
            submitButtonText={isNew ? `Create ${contentTypeLabel}` : `Update ${contentTypeLabel}`}
            submitDisabled={!isFormValid || isSubmitting}
            saveButtonText={`Save ${contentTypeLabel}`}
          />
        </div>
      </div>

      <ConfirmDialog maskStyle={{ backdropFilter: "blur(5px)" }} />

      {/* Leave / Stay dialog — required by migration skill for FormStepper pages */}
      <Dialog
        header="Unsaved Changes"
        visible={blocker.state === "blocked"}
        onHide={() => blocker.reset?.()}
        footer={
          <div className="flex justify-end gap-2">
            <Button label="Stay" severity="secondary" onClick={() => blocker.reset?.()} />
            <Button label="Leave" severity="danger" onClick={() => blocker.proceed?.()} />
          </div>
        }
      >
        <p>You have unsaved changes. Are you sure you want to leave?</p>
      </Dialog>
    </>
  );
};

export default PathsNew;
